/* eslint-disable no-console */
import {
  useGetCapacityFilters,
  useDeleteCapacityFilter,
  useGetCountyOptionsWithState,
  useGetResourcesPaginated,
  useUpsertCapacityFilter,
  useGetCompanyRoleOptions,
} from '@dispatch/shared/hooks';
import { v4 as uuidv4 } from 'uuid';
import {
  CapacityAttributes,
  CapacityFilter,
  CapacityFilterResponse,
  Company,
  LocationsWithCount,
  MultiSelectOption,
} from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';
import {
  AsyncSelect,
  ConfirmModal,
  DateRangeField,
  Form,
  FreeSoloSelect,
  Modal,
  RadioField,
  RenderFuncProps,
  SearchField,
  Section,
  SelectField,
  TextField,
  useNotification,
} from '@dispatch/web/ui';
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Control,
  FieldErrors,
  FieldValues,
  useFieldArray,
  useForm,
  UseFormWatch,
} from 'react-hook-form';
import { useDebounce } from 'react-use';

import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty, includes, isNil, isArray } from 'lodash';
import * as yup from 'yup';
import { addDays, differenceInDays, subDays } from 'date-fns';
import { yupResolver } from '@hookform/resolvers/yup';
import useUnassignedTasks from '../common/useUnassignedTasks';
import { grey } from '@mui/material/colors';

import _ from 'lodash';
import styled from '@emotion/styled';
import { useGeneralDataContext } from '@dispatch/shared/hooks';
import { CapacityAttributesForm } from './CapacityAttributesForm';

const DEBOUNCE_DELAY = 1000;
const RESOURCES_PAGE_SIZE = 25;

// Sentinel option value for the "All" row prepended to the states pickers.
// Selecting it expands the field to every real state abbreviation (see the
// pickers' onValueChange), which replaces the sentinel in the same event
// handler, so it never reaches a render or the submitted value. Longer than
// two chars, so it can never collide with a two-letter state abbreviation.
// (Deselecting all is the field's built-in clear "X" — no extra row needed.)
const ALL_STATES_VALUE = '__ALL__';

// Strip the "All" sentinel from a states/COStates array. Shared by BOTH exits
// that reach a backend — the report submit boundary and savefilter() (the
// persistence exit) — so neither can drift and leave the guard on only one.
const stripAllSentinel = (values?: string[]): string[] =>
  (values || []).filter((value) => value !== ALL_STATES_VALUE);

const SavedFiltersContainer = styled.li`
  font-size: 16px;
  .saved-filter {
    p {
      margin: 5px 0;
    }

    button {
      font-size: 13px;
    }
  }
`;

const SavedFilters = styled.div`
  padding: 0 10px;
  p {
    margin: 5px 0;
  }

  button {
    font-size: 13px;
  }
`;

const attributeSchema = yup.object().shape({
  attributeDefinitionIntegrationGUID: yup.string().optional(),

  selectedAttributeValuesVarchars: yup
    .array()
    .of(
      yup.object().shape({
        attributeDefinitionValueIntegrationGUID: yup.string().required(),
      })
    )
    .when('attributeDefinitionIntegrationGUID', {
      is: (val: unknown) => !!val, // if present
      then: (schema) => schema.min(1, 'Select at least one value').required(),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const createNumberSchema = () =>
  yup
    .number()
    .optional()
    .transform((value) =>
      isNaN(value) ||
      isNil(value) ||
      value === 'e' ||
      value === '-' ||
      value === '+'
        ? -1
        : value
    )
    .default(0);

export const CapacityFormSchema = yup.object().shape({
  locationType: yup.string().when('capacityType', {
    is: (val: string) => val !== 'ClaimOwner',
    then: yup
      .string()
      .oneOf(['state', 'county', 'zip'], 'Invalid location type')
      .required('Select a type'),
    otherwise: yup.string().nullable(), // allow blank for ClaimOwner
  }),
  unassignedTasks: yup
    .array()
    .of(
      yup.object().shape({
        location: yup.string().required('Location is required'),
        count: createNumberSchema().integer('Enter an integer'),
      })
    )
    .when('locationType', {
      is: (locationType: string) =>
        includes(['state', 'county', 'zip'], locationType),
      then: yup
        .array()
        .test(
          'not-empty',
          'Enter at least one count',
          (value) => !isEmpty(value)
        ),
    }),
  COLocationType: yup.string().when('capacityType', {
    is: 'ClaimOwner',
    then: yup.string().required('Claim Owner Location Type is required'),
    otherwise: yup.string().optional().nullable(),
  }),
  capacityType: yup
    .string()
    .oneOf(['ClaimOwner', 'Field', 'Virtual'])
    .test('not-empty', 'Select at least one type', (value) => !isEmpty(value)),
  states: yup
    .array()
    .of(yup.string())
    .when('locationType', {
      is: 'state',
      then: yup
        .array()
        .test('not-empty', 'Select at least one', (value) => !isEmpty(value)),
    }),
  counties: yup
    .array()
    .of(
      yup.object().shape({
        title: yup.string().required(),
        value: yup.string().required(),
      })
    )
    .when('locationType', {
      is: 'county',
      then: yup
        .array()
        .test('not-empty', 'Select at least one', (value) => !isEmpty(value)),
    }),
  zipCodes: yup
    .array()
    .of(yup.string().matches(/^\d{5}(-\d{4})?$/, 'Invalid ZIP code format'))
    .when('locationType', {
      is: 'zip',
      then: yup
        .array()
        .test('not-empty', 'Enter at least one', (value) => !isEmpty(value)),
    }),
  assignedResourceIds: yup
    .array()
    .of(
      yup.object().shape({
        title: yup.string().required(),
        value: yup.string().required(),
      })
    )
    .optional(),
  perils: yup
    .string()
    .nullable()
    .when('capacityType', {
      is: (capacityType: string) => capacityType === 'ClaimOwner',
      then: yup.string().nullable(),
      otherwise: yup.string().required('Select a value'),
    }),
  roles: yup
    .array()
    .of(yup.string())
    .when('capacityType', {
      is: (val: string) => val !== 'ClaimOwner',
      then: yup.array().of(yup.string()).min(1, 'Select at least one role'),
    }),
  teams: yup.array().of(yup.string()).optional().nullable(),
  attributes: yup.array().of(attributeSchema).optional(),
  assignmentTypes: yup
    .string()
    .nullable()
    .when('capacityType', {
      is: (capacityType: string) => capacityType === 'ClaimOwner',
      then: yup.string().nullable(),
      otherwise: yup
        .string()
        .oneOf(
          ['Initial', 'Supplement', 'Catastrophe', 'Non-Catastrophe'],
          'Select a valid type'
        )
        .required('Select a type'),
    }),
  dateRange: yup
    .array()
    .of(yup.date().required('Date is required'))
    .length(2, 'Enter both dates')
    .test('date-range-order', 'End date must be after start date', (value) => {
      return (
        isArray(value) &&
        value.length === 2 &&
        !isNil(value[0]) &&
        !isNil(value[1]) &&
        value[1] >= value[0]
      );
    })
    .test(
      'date-range-14-days',
      'Date range must not exceed 14 days',
      function (value) {
        const capacityType = this.parent?.capacityType;

        if (capacityType === 'ClaimOwner') {
          return true; // Skip the 14-day limit
        }

        if (Array.isArray(value) && value.length === 2) {
          const [startDate, endDate] = value;
          if (startDate && endDate) {
            return differenceInDays(endDate, startDate) <= 14;
          }
        }

        return true; // If invalid or incomplete, skip this test
      }
    ),
  durationInMinutes: createNumberSchema().when('capacityType', {
    is: (capacityType: string) =>
      capacityType === 'Field' || capacityType === 'Virtual',
    then: yup
      .number()
      .min(90, `Enter a positive number greater than or equal to 90`)
      .required('Enter a positive number greater than or equal to 90'),
  }),
  averageDriveTime: createNumberSchema().when('capacityType', {
    is: (capacityType: string) => capacityType === 'Field',
    then: yup
      .number()
      .min(1, 'Enter a positive number greater than 0')
      .required('Enter a positive number greater than 0'),
    otherwise: yup.number().optional().notRequired(),
  }),
  fieldResourcesCapacityPerDay: createNumberSchema()
    .integer('Enter an integer')
    .when(['unassignedTasks', 'capacityType'], {
      is: (
        unassignedTasks: Array<{ location: string; count: number }>,
        capacityType: string
      ) =>
        unassignedTasks &&
        unassignedTasks.some(({ count }) => count > 0) &&
        capacityType &&
        capacityType === 'Field',
      then: yup
        .number()
        .min(1, 'Enter a positive number greater than 0')
        .required('Enter a positive number greater than 0'),
    }),
  virtualResourcesCapacityPerDay: createNumberSchema()
    .integer('Enter an integer')
    .when(['unassignedTasks', 'capacityType'], {
      is: (
        unassignedTasks: Array<{ location: string; count: number }>,
        capacityType: string
      ) =>
        unassignedTasks &&
        unassignedTasks.some(({ count }) => count > 0) &&
        capacityType &&
        capacityType === 'Virtual',
      then: yup
        .number()
        .min(1, 'Enter a positive number greater than 0')
        .required('Enter a positive number greater than 0'),
    }),
});

export type CapacityFormData = {
  filterId?: string;
  filterName?: string;
  COLocationType?: string;
  COStates?: string[];
  locationType?: 'state' | 'county' | 'zip';
  unassignedTasks: Array<{ location: string; count: number }>;
  capacityType: string;
  states: string[];
  counties: Array<{ title: string; value: string }>;
  zipCodes: string[];
  assignedResourceIds: Array<{ title: string; value: string }>;
  perils: string;
  roles?: string[];
  teams: string[];
  offices?: Array<{ title: string; value: string }>;
  assignmentTypes?: string;
  dateRange: [Date | undefined, Date | undefined];
  durationInMinutes?: number;
  averageDriveTime?: number;
  fieldResourcesCapacityPerDay?: number;
  virtualResourcesCapacityPerDay?: number;
  claimOwnerResourcesCapacityPerDay?: number;
  attributes?: CapacityAttributes[];
  addSelectedAttributeGUID: MultiSelectOption[];
};
const defaultValues: CapacityFormData = {
  filterName: '',
  locationType: undefined,
  unassignedTasks: [],
  COLocationType: 'Virtual',
  capacityType: '',
  COStates: [],
  states: [],
  counties: [],
  zipCodes: [],
  assignedResourceIds: [],
  perils: '',
  roles: [],
  teams: [],
  offices: [],
  assignmentTypes: '',
  dateRange: [new Date(), new Date()],
  durationInMinutes: 0,
  averageDriveTime: 0,
  fieldResourcesCapacityPerDay: 0,
  virtualResourcesCapacityPerDay: 0,
  claimOwnerResourcesCapacityPerDay: 0,
  attributes: [],
  addSelectedAttributeGUID: [],
};

interface NumberFieldProps {
  name: string;
  label: string;
  control: Control<FieldValues, CapacityFormData>;
  errors: FieldErrors<CapacityFormData>;
  defaultValue?: number;
  dataTestId: string;
}

const NumberField: React.FC<NumberFieldProps> = ({
  name,
  label,
  control,
  errors,
  defaultValue = 0,
  dataTestId,
}) => {
  return (
    <Grid item xs={12} sm={6}>
      <TextField
        id={name}
        name={name}
        label={label}
        control={control}
        errors={errors}
        type="text"
        sx={{ width: '100%' }}
        data-testid={dataTestId}
        defaultValue={defaultValue}
        inputLabelProps={{
          shrink: true,
        }}
        inputAttributes={{
          min: 0,
          step: 1,
          inputmode: 'numeric',
          pattern: '[0-9]*',
        }}
      />
    </Grid>
  );
};

const LocationTaskCountTable = ({
  items,
  control,
  errors,
}: {
  items: Array<string | MultiSelectOption>;
  control: Control<FieldValues, CapacityFormData>;
  errors: FieldErrors<CapacityFormData>;
}) => {
  const { fields } = useFieldArray({
    control: control,
    name: `unassignedTasks`,
  });

  return (
    <TableContainer component={Paper} data-testid="location-task-count-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Location</TableCell>
            <TableCell>Projected Field Task Count</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field, index) => {
            const label =
              typeof items[index] === 'string'
                ? items[index]
                : (items[index] as MultiSelectOption)?.title;
            const fieldName = `unassignedTasks.${index}.count`;
            return (
              <TableRow key={field.id}>
                <TableCell>{label}</TableCell>
                <TableCell>
                  <TextField
                    id={fieldName}
                    name={fieldName}
                    label="Count"
                    control={control}
                    error={errors.unassignedTasks?.[index]?.count?.message}
                    data-testid={`${fieldName}-taskCount-input`}
                    type="text"
                    defaultValue={0}
                    inputLabelProps={{
                      shrink: true,
                    }}
                    inputAttributes={{
                      min: 0,
                      step: 1,
                      inputmode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export type CapacityDashboardFiltersProps = {
  assignmentTypeOptions: Partial<MultiSelectOption>[];
  companyTeams: MultiSelectOption[];
  isFilterDrawerOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CapacityFormData) => void;
  company?: Company;
  perils?: MultiSelectOption[];
  offices?: MultiSelectOption[];
};

export function CapacityDashboardFilters({
  assignmentTypeOptions,
  companyTeams,
  isFilterDrawerOpen,
  onClose,
  onSubmit,
  company,
  perils,
  offices,
}: CapacityDashboardFiltersProps) {
  const form = useForm<CapacityFormData>({
    resolver: yupResolver(CapacityFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const { setValue, getValues } = form;

  const {
    fields: attributeFields,
    append: appendAttribute,
    remove: removeAttribute,
  } = useFieldArray({
    control: form.control,
    name: 'attributes',
  });

  const { tenantId, userId } = useCurrentUser();
  const { statesOptions } = useGeneralDataContext();

  // Prepend an "All" row as the first choice in the states pickers. Selecting it
  // populates the field with every state (see applyStatesSentinel), so a
  // dispatcher can run the report across all states in one click instead of ~50.
  // Only prepend once the states have loaded — otherwise choosing "All" would
  // expand to an empty list, clearing the field and firing "Select at least one".
  const statesPickerOptions: MultiSelectOption[] = React.useMemo(
    () =>
      statesOptions && statesOptions.length > 0
        ? [{ title: 'All', value: ALL_STATES_VALUE }, ...statesOptions]
        : statesOptions || [],
    [statesOptions]
  );

  // When the "All" row is chosen, replace the field's value with every real
  // state abbreviation (dropping the sentinel). Wired to both states pickers'
  // onValueChange so "All" is equivalent to selecting all states individually.
  //
  // Intentionally ONE-WAY (product decision on #100889): "All" only expands, so
  // it never renders as a checked row, and deselecting everything is the field's
  // built-in clear "X" (default clearable) — the same affordance as the panel's
  // other multiselects — not a re-click toggle on this row.
  const applyStatesSentinel = React.useCallback(
    (
      fieldName: 'states' | 'COStates',
      value: MultiSelectOption | (string | number)[] | null
    ): void => {
      if (Array.isArray(value) && value.includes(ALL_STATES_VALUE)) {
        form.setValue(
          fieldName,
          (statesOptions || []).map((option) => String(option.value)),
          { shouldValidate: true }
        );
      }
    },
    [form, statesOptions]
  );

  const companyTeamOptions = React.useMemo(() => {
    return companyTeams;
  }, [companyTeams]);
  const { companyRoles } = useGetCompanyRoleOptions(company?.role || []);
  const { storedFilters, fetchStoredFilters } = useGetCapacityFilters(
    userId || ''
  );
  const { notifyError, notifySuccess } = useNotification();

  const onCapacityFilterDelete = () => {
    notifySuccess('Filter Deleted', 'Filter has been successfully deleted');
    if (selectedFilter?.filterName == form.getValues('filterName')) {
      form.reset();
    }
    fetchStoredFilters?.();
  };

  const onComplete = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess('Filter Saved', 'Filter has been successfully saved');
      fetchStoredFilters?.();
      //To disable save
      setAppliedFilterFormData(form.getValues());
    } else {
      notifyError('Error', 'Filter was not saved');
    }
  };

  const { deleteCapacityFilter } = useDeleteCapacityFilter(
    userId || '',
    onCapacityFilterDelete
  );

  const { upsertCapacityFilter } = useUpsertCapacityFilter(
    onComplete,
    userId || ''
  );

  const [countyInput, setCountyInput] = useState('');
  const [openFavoritesModal, setOpenFavoritesModal] = useState(false);
  const [debouncedCountyInput, setDebouncedCountyInput] = useState('');
  useDebounce(() => setDebouncedCountyInput(countyInput), DEBOUNCE_DELAY, [
    countyInput,
  ]);

  const [resourceInput, setResourceInput] = useState('');
  const [filteredFavorites, setFilteredFavorites] = useState<CapacityFilter[]>(
    []
  );
  const [appliedFilterFormData, setAppliedFilterFormData] =
    useState<CapacityFormData>();
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<CapacityFilter>();
  const [debouncedResourceInput, setDebouncedResourceInput] = useState('');
  useDebounce(() => setDebouncedResourceInput(resourceInput), DEBOUNCE_DELAY, [
    resourceInput,
  ]);

  const filteredCounties = useGetCountyOptionsWithState(
    debouncedCountyInput,
    !!debouncedCountyInput
  );
  const filteredResources = useGetResourcesPaginated({
    filters: {
      resourceName: debouncedResourceInput,
      isActive: true,
      pageSize: RESOURCES_PAGE_SIZE,
    },
    enabled: !!debouncedResourceInput,
  });

  const mappedFilteredResources = useMemo(() => {
    if (filteredResources.status === 'success' && filteredResources.data) {
      return filteredResources.data.map(
        (resource): MultiSelectOption => ({
          title: resource.name,
          value: resource.integrationGUID,
        })
      );
    }
    return []; // Return an empty array as the default value
  }, [filteredResources.data, filteredResources.status]);

  // handles conserving the count values when changing location type
  useUnassignedTasks({ form, selectedFilter });

  const locationTypeWatchValue = form.watch('locationType');
  const statesWatchValue = form.watch('states');
  const countiesWatchValue = form.watch('counties');
  const zipCodesWatchValue = form.watch('zipCodes');
  const resourcesWatchValue = form.watch('assignedResourceIds');
  const teamsWatchValue = form.watch('teams');
  const officesWatchValue = form.watch('offices');

  const filterNameWatchValue = form.watch('filterName');

  const capacityType = form.watch('capacityType');

  useEffect(() => {
    setFilteredFavorites(storedFilters || []);
  }, [storedFilters]);

  useEffect(() => {
    const locationType = form.getValues('locationType');
    const selectedLocations = form.getValues(
      locationType === 'state'
        ? 'states'
        : locationType === 'county'
        ? 'counties'
        : 'zipCodes'
    );
    const selectedUnAssignedTasks = form.getValues('unassignedTasks');

    if (locationType && selectedLocations) {
      const newUnassignedTasks = selectedLocations.map((location, index) => {
        const loc = typeof location === 'string' ? location : location.title;
        const count = selectedUnAssignedTasks
          ? selectedUnAssignedTasks.find(
              (x) =>
                (typeof x.location === 'string'
                  ? x.location
                  : x.location['title']) == loc
            )?.count || 0
          : 0;
        return {
          location: loc,
          count: count,
        };
      });
      form.setValue('unassignedTasks', newUnassignedTasks, {
        shouldValidate: true,
      });
    }
  }, [
    form,
    locationTypeWatchValue,
    statesWatchValue,
    countiesWatchValue,
    zipCodesWatchValue,
  ]);

  const locationType = form.watch('locationType');
  const isRequired = React.useMemo(() => {
    if (capacityType === 'ClaimOwner') {
      return false;
    }
    return true;
  }, [capacityType]);

  const getFilteredOptions = () => {
    const formAttributes = form.watch('attributes');
    const companyAttributes = company?.attributeDefinition;

    if (!formAttributes || !companyAttributes) {
      return (
        companyAttributes?.map((value) => {
          return {
            title: value.displayName,
            value: value.integrationGUID,
          };
        }) || []
      );
    }

    const filteredCompanyAttributes = companyAttributes?.filter(
      (companyAttr) => {
        return !formAttributes.find(
          (formAttr) =>
            formAttr.attributeDefinitionIntegrationGUID ===
            companyAttr.integrationGUID
        );
      }
    );

    return filteredCompanyAttributes.map((value) => {
      return {
        title: value.displayName,
        value: value.integrationGUID,
      };
    });
  };

  const handleAddAttribute = (value: unknown) => {
    const rawValue = value as { title: string; value: string };
    const selectedAttribute = getSelectedAttributeData(rawValue.value);

    appendAttribute(selectedAttribute);
  };

  type FormAttribute = {
    integrationGUID: string;
    attributeDefinitionIntegrationGUID: string;
    displayName: string;
    selectedAttributeValuesVarchars: {
      selectedAttributeDefinitionIntegrationGUID: string;
      attributeDefinitionValueIntegrationGUID: string;
      title: string;
      value: string;
      displayName?: string;
    }[];
  };

  const getSelectedAttributeData = (integrationGUID: string): FormAttribute => {
    const selectedAttribute = company?.attributeDefinition?.find(
      (attribute) => attribute.integrationGUID === integrationGUID
    );

    return {
      integrationGUID: uuidv4(),
      attributeDefinitionIntegrationGUID:
        selectedAttribute?.integrationGUID || '',
      displayName: selectedAttribute?.displayName || '',
      selectedAttributeValuesVarchars: [],
    };
  };

  React.useEffect(() => {
    if (form.watch('capacityType') === 'ClaimOwner') {
      form.setValue('dateRange', [new Date(), new Date()]);
    }
  }, [form.watch('capacityType')]);

  const today = new Date();
  const minDate = subDays(today, 14);
  const maxDate = addDays(today, 14);

  const renderForm = React.useCallback(
    ({ control, errors }: RenderFuncProps) => {
      return (
        <Stack spacing={5} sx={{ p: 3, height: 'auto' }}>
          <Section title="Filters">
            <Stack spacing={2}>
              <TextField
                id="filterName"
                name="filterName"
                label="Filter Name"
                control={control}
              />
              <SelectField
                id="capacityType"
                name="capacityType"
                label="Capacity Type"
                control={control}
                errors={errors}
                required
                options={[
                  { title: 'Claim Owner', value: 'ClaimOwner' },
                  { title: 'Field', value: 'Field' },
                  { title: 'Virtual', value: 'Virtual' },
                ]}
                data-testid="capacityType-field"
              />
              <DateRangeField
                id="dateRange"
                name="dateRange"
                label="Date Range"
                control={control}
                minDate={capacityType === 'ClaimOwner' ? minDate : undefined}
                maxDate={capacityType === 'ClaimOwner' ? maxDate : undefined}
                disablePast={capacityType !== 'ClaimOwner'}
                startLabel="Start"
                endLabel="End"
                data-testid="dateRange-field"
              />
              {!isEmpty(errors['dateRange']) ? (
                <FormHelperText error data-testid="dateRange-error">
                  {errors['dateRange'].message as string}
                </FormHelperText>
              ) : null}
              {capacityType === 'ClaimOwner' ? (
                <>
                  <RadioField
                    row
                    control={control}
                    id="COLocationType"
                    name="COLocationType"
                    defaultValue={'Virtual'}
                    buttons={[
                      {
                        label: 'Virtual',
                        value: 'Virtual',
                      },
                      // {
                      //   label: 'Field',
                      //   value: 'Field',
                      // },
                    ]}
                  />
                  <SelectField
                    limitTags={1}
                    addScroll
                    id="COStates"
                    name="COStates"
                    label="States"
                    control={control}
                    errors={errors}
                    multiple
                    options={statesPickerOptions}
                    onValueChange={(value) =>
                      applyStatesSentinel('COStates', value)
                    }
                    data-testid="states-field"
                  />
                </>
              ) : (
                <SelectField
                  id="locationType"
                  name="locationType"
                  label="Location Type"
                  required
                  onValueChange={(selectedLocationType) => {
                    if (
                      typeof selectedLocationType !== 'string' ||
                      !['state', 'county', 'zip'].includes(selectedLocationType)
                    ) {
                      return;
                    }

                    if (selectedLocationType === 'state') {
                      form.setValue('states', []);
                    } else if (selectedLocationType === 'county') {
                      form.setValue('counties', []);
                    } else if (selectedLocationType === 'zip') {
                      form.setValue('zipCodes', []);
                    }
                  }}
                  control={control}
                  errors={errors}
                  options={[
                    { value: 'state', title: 'State' },
                    { value: 'county', title: 'County' },
                    { value: 'zip', title: 'Zip' },
                  ]}
                  data-testid="locationType-field"
                />
              )}
              {!isEmpty(locationType) && capacityType !== 'ClaimOwner' ? (
                <Stack sx={{ paddingLeft: 3 }}>
                  {locationType === 'state' && (
                    <SelectField
                      limitTags={1}
                      addScroll
                      id="states"
                      name="states"
                      label="States"
                      control={control}
                      errors={errors}
                      multiple
                      options={statesPickerOptions}
                      onValueChange={(value) =>
                        applyStatesSentinel('states', value)
                      }
                      data-testid="states-field"
                    />
                  )}
                  {locationType === 'county' && (
                    <AsyncSelect
                      id="counties"
                      name="counties"
                      label="Counties"
                      control={control}
                      errors={errors}
                      loading={filteredCounties?.status === 'loading'}
                      multiple
                      options={filteredCounties?.data || []}
                      setInput={setCountyInput}
                      data-testid="counties-field"
                    />
                  )}
                  {locationType === 'zip' && (
                    <FreeSoloSelect
                      id="zipCodes"
                      name="zipCodes"
                      label="Zip Codes"
                      control={control}
                      errors={errors}
                      pattern={/^[0-9]{5}((-[0-9]{4})|(-[0-9]{2}))?$/}
                      data-testid="zipCodes-field"
                    />
                  )}
                  {isRequired &&
                    renderLocationProjectedTaskCounts(
                      control,
                      errors,
                      form.watch
                    )}
                </Stack>
              ) : null}

              {isRequired && (
                <>
                  <SelectField
                    id="perils"
                    name="perils"
                    label="Peril"
                    required
                    control={control}
                    errors={errors}
                    options={perils || []}
                    data-testid="peril-field"
                  />
                  <SelectField
                    id="assignmentTypes"
                    name="assignmentTypes"
                    label="Assignment Type"
                    required
                    control={control}
                    errors={errors}
                    options={
                      (assignmentTypeOptions as MultiSelectOption[]) || []
                    }
                    data-testid="assignmentType-field"
                  />
                </>
              )}
              {isRequired && (
                <SelectField
                  multiple
                  id="roles"
                  name="roles"
                  label="Roles"
                  required={capacityType !== 'ClaimOwner'}
                  control={control}
                  errors={errors}
                  options={companyRoles || []}
                  data-testid="roles-field"
                />
              )}
              <AsyncSelect
                disabled={
                  !isEmpty(teamsWatchValue) || !isEmpty(officesWatchValue)
                }
                control={control}
                errors={errors}
                id="assignedResourceIds"
                name="assignedResourceIds"
                label="Resources"
                loading={filteredResources?.status === 'loading'}
                options={mappedFilteredResources || []}
                tagLimit={10}
                setInput={setResourceInput}
                multiple
                data-testid="assignedResourceIds-field"
                onValueChange={() => {
                  form.setValue('teams', []);
                  form.setValue('offices', []);
                }}
              />
              <SelectField
                disabled={
                  !isEmpty(resourcesWatchValue) || !isEmpty(officesWatchValue)
                }
                control={control}
                errors={errors}
                multiple
                id="teams"
                name="teams"
                label="Teams"
                options={companyTeamOptions || []}
                data-testid="teamIds-field"
                onValueChange={() => {
                  form.setValue('assignedResourceIds', []);
                  form.setValue('offices', []);
                }}
              />
              {capacityType === 'ClaimOwner' && (
                <SelectField
                  disabled={
                    !isEmpty(resourcesWatchValue) || !isEmpty(teamsWatchValue)
                  }
                  multiple
                  id="offices"
                  name="offices"
                  label="Offices"
                  control={control}
                  errors={errors}
                  options={offices || []}
                  onValueChange={() => {
                    form.setValue('teams', []);
                    form.setValue('assignedResourceIds', []);
                  }}
                />
              )}
            </Stack>
          </Section>
          {capacityType === 'ClaimOwner' && (
            <Section title="Resource Attributes">
              <SelectField
                id="addSelectedAttributeGUID"
                name="addSelectedAttributeGUID"
                label="Add New Attribute"
                control={control}
                onValueChange={handleAddAttribute}
                options={getFilteredOptions()}
              />
              <Divider
                sx={{ my: 1, borderColor: '#ccc', borderBottomWidth: 1 }}
              />
              {attributeFields.map((field, index) => (
                <CapacityAttributesForm
                  key={`${field.id}`}
                  control={control}
                  index={index}
                  removeAttribute={removeAttribute}
                  watch={form.watch}
                  field={field}
                  errors={errors}
                  setValue={setValue}
                  getValues={getValues}
                />
              ))}
              {/* needed or button will hide inputs */}
              <Grid item xs={12} sm={6} sx={{ height: '100px' }}></Grid>
            </Section>
          )}
          {isRequired && (
            <Section title="Resource Planning">
              <Grid container spacing={2}>
                <NumberField
                  name="durationInMinutes"
                  label="Task Duration (minutes)"
                  control={control}
                  errors={errors}
                  dataTestId="durationInMinutes-field"
                />
                <NumberField
                  name="averageDriveTime"
                  label="Average Drive Time (minutes)"
                  control={control}
                  errors={errors}
                  dataTestId="averageDriveTime-field"
                />
                <NumberField
                  name="fieldResourcesCapacityPerDay"
                  label="Field Task Capacity Per Day"
                  control={control}
                  errors={errors}
                  dataTestId="fieldResourcesCapacityPerDay-field"
                />
                <NumberField
                  name="virtualResourcesCapacityPerDay"
                  label="Virtual Task Capacity Per Day"
                  control={control}
                  errors={errors}
                  dataTestId="virtualResourcesCapacityPerDay-field"
                />
              </Grid>
              {/* needed or button will hide inputs */}
              <Grid item xs={12} sm={6} sx={{ height: '100px' }}></Grid>
            </Section>
          )}
        </Stack>
      );
    },
    [
      form,
      companyRoles,
      companyTeams,
      companyTeamOptions,
      perils,
      filteredCounties,
      filteredResources,
      statesPickerOptions,
      applyStatesSentinel,
    ]
  );

  const renderLocationProjectedTaskCounts = (
    control: Control<FieldValues, CapacityFormData>,
    errors: FieldErrors<CapacityFormData>,
    watch: UseFormWatch<CapacityFormData>
  ) => {
    const locationType = watch('locationType');
    const states = watch('states');
    const counties = watch('counties');
    const zipCodes = watch('zipCodes');

    switch (locationType) {
      case 'state':
        return (
          <LocationTaskCountTable
            items={states || []}
            control={control}
            errors={errors}
            data-testid="locationTaskCountTable-state"
          />
        );
      case 'county':
        return (
          <LocationTaskCountTable
            items={counties || []}
            control={control}
            errors={errors}
            data-testid="locationTaskCountTable-county"
          />
        );
      case 'zip':
        return (
          <LocationTaskCountTable
            items={zipCodes || []}
            control={control}
            errors={errors}
            data-testid="locationTaskCountTable-zip"
          />
        );
      default:
        return null;
    }
  };

  const SearchByName = (value: string) => {
    if (!isEmpty(value)) {
      const filteredData = storedFilters?.filter((data: CapacityFilter) =>
        data?.filterName?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredFavorites(filteredData || []);
    } else {
      setFilteredFavorites(storedFilters || []);
    }
  };

  const RenderModalContent = () => {
    return (
      <Stack
        justifyContent="space-between"
        height="450px"
        p={2}
        overflow="auto"
      >
        <Stack
          data-testid="favorites-modal-list"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap={3}>
            <Typography variant="body1" sx={{ color: '#303F9F' }}>
              Name
            </Typography>
            <SearchField
              backgroundColor={grey[50]}
              searchIconColor={grey[900]}
              clearButtonColor={grey[900]}
              textColor={grey[900]}
              onSearch={(value: string) => SearchByName(value)}
            />
          </Stack>
        </Stack>
        <Box
          height={500}
          data-testid="favorites-list"
          sx={{ overflow: 'auto' }}
        >
          {favorites()}
        </Box>
      </Stack>
    );
  };

  const favorites = () => {
    if (filteredFavorites && filteredFavorites?.length > 0) {
      //setFilteredFavorites(storedFilters);
      return (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            maxHeight: '135px',
          }}
        >
          {renderSavedFilters()}
        </ul>
      );
    }

    return <p style={{ textAlign: 'center' }}>No saved filters</p>;
  };

  const fillFilterFields = (selectedfilter: CapacityFilter) => {
    form.setValue?.('filterId', selectedfilter?.filterId);
    form.setValue?.('filterName', selectedfilter?.filterName);
    form.setValue?.('durationInMinutes', selectedfilter?.durationInMinutes);
    form.setValue?.('averageDriveTime', selectedfilter?.driveTimeInMinutes);
    form.setValue?.(
      'fieldResourcesCapacityPerDay',
      selectedfilter?.resourceCapacityPerDay?.field
    );
    form.setValue?.(
      'virtualResourcesCapacityPerDay',
      selectedfilter?.resourceCapacityPerDay?.virtual
    );
    form.setValue?.(
      'claimOwnerResourcesCapacityPerDay',
      selectedfilter?.resourceCapacityPerDay?.claimOwner
    );
    if (selectedfilter?.perilIds) {
      form.setValue?.('perils', selectedfilter?.perilIds);
    }

    form.setValue('assignmentTypes', selectedfilter?.assignmentTypes || '');
    form.setValue('capacityType', selectedfilter?.capacityType || '');
    form.setValue('assignedResourceIds', selectedfilter?.resourceIds || []);
    form.setValue('locationType', selectedfilter?.locations?.[0]?.type);
    //if its a claim owner capacityType -
    // set the radio field as Virtual, other wise set the select field
    //set roles = [], states will be picked up from COstates field
    if (selectedfilter?.capacityType === 'ClaimOwner') {
      form.setValue(
        'COLocationType',
        selectedfilter?.claimOwnerLocationType || ''
      ); //radio btn
      form.setValue('offices', selectedfilter?.officeIds || []);
      form.setValue('roles', []);
      //set COStates
      form.setValue(
        'COStates',
        selectedfilter?.locations?.map((x: any) => x?.state)
      );
      //needed to get past validation, TODO: fix this so we don't need to set states for ClaimOwner
      form.setValue(
        'states',
        selectedfilter?.locations?.map((x: any) => x?.state) || []
      );
    } else {
      form.setValue('roles', selectedfilter?.roleIds || []);
      form.setValue('offices', []);
      const filterStates =
        (selectedfilter?.locations
          ?.map((x: LocationsWithCount) => {
            if (x.type === 'state') {
              return x?.state;
            }
          })
          .filter((x) => x !== undefined) as string[]) || [];
      const filterCounties =
        (selectedfilter?.locations
          ?.map((x: LocationsWithCount) => {
            if (x.type === 'county') {
              return x?.county;
            }
          })
          .filter((x) => x !== undefined) as {
          title: string;
          value: string;
        }[]) || [];
      const filterZipCodes =
        (selectedfilter?.locations
          ?.map((x: LocationsWithCount) => {
            if (x.type === 'zip') {
              return x?.zip;
            }
          })
          .filter((x) => x !== undefined) as string[]) || [];
      const filterUnAssignedTasksCount =
        (selectedfilter?.locations
          ?.map((x: LocationsWithCount) => {
            if (x.type === 'state') {
              return { location: x.state || '', count: x.unassignedTasks };
            }
            if (x.type === 'county') {
              return {
                location: x.county?.title || '',
                count: x.unassignedTasks,
              };
            }
            if (x.type === 'zip') {
              return { location: x.zip || '', count: x.unassignedTasks };
            }
          })
          .filter((x) => x !== undefined) as {
          location: string;
          count: number;
        }[]) || [];
      form.setValue('states', filterStates);
      form.setValue('counties', filterCounties);
      form.setValue('zipCodes', filterZipCodes);
      form.setValue('unassignedTasks', filterUnAssignedTasksCount);
    }
    form.setValue('teams', selectedfilter?.teamIds || []);
    form.setValue('attributes', selectedfilter?.resourceAttributes || []);
  };

  const renderSavedFilters = () => {
    return filteredFavorites?.map((filter: CapacityFilter, index: number) => {
      return (
        <SavedFiltersContainer key={`${filter.filterId}_${index}`}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <SavedFilters>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <p style={{ width: '30%', overflowWrap: 'break-word' }}>
                  {filter?.filterName}
                </p>
                <Button
                  variant="contained"
                  type="button"
                  data-testid={`apply-favourites-${filter.filterName}`}
                  onClick={() => {
                    setOpenFavoritesModal(false);
                    setSelectedFilter(filter);
                    fillFilterFields(filter);
                    setAppliedFilterFormData(form.getValues());
                  }}
                >
                  Apply Filter
                </Button>
                <Button
                  onClick={() => {
                    setOpenConfirmDeleteModal(true);
                    setSelectedFilter(filter);
                  }}
                  data-testid={`delete-favourites-${filter.filterName}`}
                  variant="text"
                  sx={{
                    fontSize: '10px',
                    minWidth: '55px',
                    height: '24px',
                    marginLeft: 'auto',
                  }}
                >
                  <DeleteIcon sx={{ width: '18px', height: '18px' }} />
                </Button>
              </div>
            </SavedFilters>
          </div>
          {storedFilters && index !== storedFilters?.length - 1 ? <hr /> : null}
        </SavedFiltersContainer>
      );
    });
  };

  const deleteFilter = () => {
    setOpenConfirmDeleteModal(false);
    deleteCapacityFilter(selectedFilter?.filterId || '');
    setSelectedFilter(undefined);
  };

  const renderConfirmDelete = () => {
    return (
      <ConfirmModal
        onConfirm={() => deleteFilter()}
        onDeny={() => setOpenConfirmDeleteModal(false)}
        confirmTitle={'Delete Filter?'}
        confirmContent={'Do you really want to delete the Filter?'}
        modalType={'delete'}
      />
    );
  };

  const savefilter = () => {
    const formData = form.getValues();

    let locationsData;
    if (formData.capacityType === 'ClaimOwner') {
      // Use COStates for locations if capacityType is ClaimOwner. Strip the "All"
      // sentinel first so a poisoned value can never be persisted into a saved
      // filter (the durable exit — fillFilterFields re-seeds COStates from it).
      locationsData = stripAllSentinel(formData.COStates).map((stateVal) => ({
        type: 'state',
        state: stateVal,
        unassignedTasks: 0,
      }));
    } else {
      locationsData = formData.unassignedTasks
        .filter((x) => x.location !== ALL_STATES_VALUE)
        .map((x) => ({
          type: formData.locationType,
          state: formData.locationType === 'state' ? x.location : undefined,
          county:
            formData.locationType === 'county'
              ? { title: x.location, value: x.location }
              : undefined,
          zip: formData.locationType === 'zip' ? x.location : undefined,
          unassignedTasks: x.count,
        }));
    }
    const filterDataToSave: CapacityFilterResponse = {
      filterId: formData?.filterId,
      filterName: formData?.filterName ?? '',
      startDate: formData.dateRange[0]?.toISOString(),
      endDate: formData.dateRange[1]?.toISOString(),
      locations: JSON.stringify(locationsData),
      capacityType: formData.capacityType,
      perilIds: JSON.stringify(formData.perils),
      assignmentTypes: JSON.stringify(formData.assignmentTypes),
      roleIds: JSON.stringify(formData.roles),
      resourceIds: JSON.stringify(formData.assignedResourceIds),
      teamIds: JSON.stringify(formData.teams),
      durationInMinutes: formData.durationInMinutes,
      driveTimeInMinutes: formData.averageDriveTime,
      resourceCapacityPerDay: JSON.stringify({
        field: formData.fieldResourcesCapacityPerDay ?? 0,
        virtual: formData.virtualResourcesCapacityPerDay ?? 0,
        claimOwner: formData.claimOwnerResourcesCapacityPerDay ?? 0,
      }),
      officeIds: JSON.stringify(formData.offices),
      claimOwnerLocationType: formData.COLocationType,
      resourceAttributes: JSON.stringify(formData.attributes),
    };
    upsertCapacityFilter(filterDataToSave);
  };

  const disableSaveFilter = useCallback(() => {
    if (isEmpty(filterNameWatchValue)) {
      return true;
    } else if (
      appliedFilterFormData &&
      _.isEqual(appliedFilterFormData, form.getValues())
    ) {
      return true;
    }
    return false;
  }, [form.watch()]);

  return (
    <Drawer
      open={isFilterDrawerOpen}
      onClose={onClose}
      anchor="right"
      data-testid="capacityDashboardFilters-drawer"
      PaperProps={{
        sx: {
          width: {
            xs: '40%',
            sm: '40%',
          },
          maxWidth: '1200px',
        },
      }}
    >
      <Stack
        direction="column"
        justifyContent="space-between"
        alignItems="stretch"
        sx={{
          minHeight: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        <Form<CapacityFormData>
          errors={form.formState.errors}
          control={form.control}
          handleSubmit={form.handleSubmit}
          onSubmit={(values) => {
            // Defensive guard: strip the "All" sentinel from the report-bound
            // fields before submitting. Normally applyStatesSentinel has already
            // expanded it away, but filtering here makes "the sentinel never
            // reaches the API" structural rather than dependent on SelectField's
            // internal onChange/onValueChange ordering.
            const sanitized: CapacityFormData = {
              ...values,
              states: stripAllSentinel(values.states),
              COStates: stripAllSentinel(values.COStates),
              unassignedTasks: (values.unassignedTasks || []).filter(
                (task) => task.location !== ALL_STATES_VALUE
              ),
            };
            // Force validation before submitting
            form.trigger().then((isValid) => {
              if (isValid) {
                onClose();
                onSubmit(sanitized);
              }
            });
            return sanitized;
          }}
          buttons={[
            {
              type: 'submit',
              label: 'Generate Report',
            },
            {
              type: 'button',
              label: 'Favorites',
              sx: { marginLeft: 'auto' },
              action: () => setOpenFavoritesModal(true),
            },
            {
              type: 'button',
              label: 'Save Filter',
              isDisabled: disableSaveFilter(),
              action: () => {
                savefilter();
              },
            },
            {
              type: 'button',
              label: 'Reset',
              isDisabled: false,
              action: () => {
                setSelectedFilter(undefined);
                setAppliedFilterFormData(undefined);
                form.reset();
              },
            },
          ]}
          render={renderForm}
          data-testid="capacityDashboardFilters-form"
        />
        <Modal
          open={openFavoritesModal}
          title="Favorites"
          handleClickAway={() => setOpenFavoritesModal(false)}
        >
          {RenderModalContent()}
          {openConfirmDeleteModal && renderConfirmDelete()}
        </Modal>
      </Stack>
    </Drawer>
  );
}
